/**
 * WidgetPreview — a single card on the dashboard builder canvas.
 *
 * Extracted from DashboardBuilderPage.js (iter-150) to keep the page
 * component focused. Responsibilities:
 *  - Render the widget's chart (bar/line/area/pie/kpi/small_multiples/table)
 *    against sample data.
 *  - Apply the incoming cross-filter payload when the widget opts in as
 *    a receiver.
 *  - Emit cross-filter payloads when the widget is a source and the user
 *    clicks a data point.
 *  - Execute per-widget click behaviour (drill/URL/AI Explore hand-off).
 *  - Overlay a comparison series + delta chip when `config.compare_with`
 *    points at another widget.
 */
import React from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import { Move, Trash2, Table, Hash } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { WIDGET_TYPES, SAMPLE_DATA, COLORS } from './widgetShared';

export function WidgetPreview({ widget, compareWidget, crossFilters = [], isSelected, onSelect, onRemove }) {
  const widgetDef = WIDGET_TYPES.find(w => w.type === widget.widget_type);
  const Icon = widgetDef?.icon || Hash;
  const isSource = !!widget.config?.cross_filter_source;
  const isReceiver = !!widget.config?.cross_filter_receive;
  const clickActionType = widget.config?.click_action?.type;

  // Iter-149 — apply incoming cross-filter payload if this widget opted
  // in to receive filters. Matches on the row's `name` (or `month`) key
  // to the filter value. Falls back to identity when no filter matches.
  const applyCrossFilter = (rows, keyGuess = ['name', 'month']) => {
    if (!isReceiver || !crossFilters || crossFilters.length === 0) return rows;
    const wanted = new Set(crossFilters.map(f => String(f.value)));
    const keys = Array.isArray(keyGuess) ? keyGuess : [keyGuess];
    const filtered = rows.filter(r => keys.some(k => wanted.has(String(r[k]))));
    // If the filter matches nothing (dimension mismatch), keep the full
    // dataset so the card doesn't blank out — this is a UX signal that
    // the source's dimension doesn't overlap this widget's schema.
    return filtered.length > 0 ? filtered : rows;
  };

  // Iter-149 — build a comparison series from another widget's data.
  const buildCompareData = (baseRows) => {
    if (!compareWidget || !baseRows?.length) return null;
    const compareType = compareWidget.widget_type;
    const cmpRows = compareType === 'bar_chart' ? SAMPLE_DATA.loss_ratio
                  : compareType === 'line_chart' ? SAMPLE_DATA.claims_trend
                  : compareType === 'pie_chart'  ? SAMPLE_DATA.loss_ratio
                  : compareType === 'kpi'        ? []
                  : SAMPLE_DATA.premium_trend;
    if (compareType === 'kpi') return null;
    const keys = ['name', 'month'];
    const key = keys.find(k => baseRows[0]?.[k] !== undefined && cmpRows[0]?.[k] !== undefined);
    if (!key) return null;
    const byKey = Object.fromEntries(cmpRows.map(r => [r[key], r]));
    const cmpNumericKey = ['value', 'claims', 'Auto', 'Homeowners'].find(k => byKey[baseRows[0][key]]?.[k] !== undefined);
    if (!cmpNumericKey) return null;
    return baseRows.map(r => ({ ...r, compare_value: byKey[r[key]]?.[cmpNumericKey] }));
  };

  const handleChartClick = (payload) => {
    if (isSource && payload && payload.name != null) {
      try {
        window.dispatchEvent(new CustomEvent('inzara.crossfilter', {
          detail: {
            source_widget: widget.widget_id,
            filters: [{ field: widget.config?.cross_filter_field || 'name', operator: 'equals', value: payload.name }],
          },
        }));
      } catch (e) { /* ignore */ }
    }
    if (clickActionType && widget.config?.click_action?.target) {
      const target = widget.config.click_action.target;
      const label = (payload && payload.name != null) ? String(payload.name) : '';
      if (clickActionType === 'drill_dashboard') {
        window.location.href = `/dashboards/${encodeURIComponent(target)}`;
      } else if (clickActionType === 'open_url') {
        window.open(target.replace('$CLICKED_LABEL', encodeURIComponent(label)), '_blank', 'noopener');
      } else if (clickActionType === 'ai_explore') {
        const prompt = target.replace('$CLICKED_LABEL', label);
        try { sessionStorage.setItem('inzara.explore.seedPrompt', prompt); } catch (e) { /* storage disabled */ }
        window.location.href = '/ai-copilot';
      }
    }
  };

  const renderChart = () => {
    switch (widget.widget_type) {
      case 'kpi': {
        const primary = widget.config?.value || '$4.2B';
        const cmp = compareWidget?.config?.value || (compareWidget ? '$4.2B' : null);
        return (
          <div className="flex flex-col items-center justify-center h-full">
            <p className="text-3xl font-heading font-bold text-foreground">{primary}</p>
            <p className="text-xs text-emerald-400 mt-1">{widget.config?.trend || '+6.8%'}</p>
            {cmp && (
              <div className="mt-2 text-[10px] text-muted-foreground flex items-center gap-1" data-testid="kpi-compare">
                <span className="opacity-70">vs</span>
                <span className="font-mono">{cmp}</span>
                <span className="px-1 rounded bg-indigo-500/15 text-indigo-300 border border-indigo-500/30">Δ</span>
              </div>
            )}
          </div>
        );
      }
      case 'bar_chart': {
        const rows = applyCrossFilter(SAMPLE_DATA.loss_ratio, 'name');
        const withCompare = buildCompareData(rows);
        const data = withCompare || rows;
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} onClick={(state) => state?.activePayload?.[0] && handleChartClick(state.activePayload[0].payload)}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#a1a1aa' }} axisLine={false} />
              <YAxis tick={{ fontSize: 9, fill: '#a1a1aa' }} axisLine={false} />
              <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} style={{ cursor: (isSource || clickActionType) ? 'pointer' : 'default' }} />
              {withCompare && <Bar dataKey="compare_value" fill="#a78bfa" radius={[4, 4, 0, 0]} name={compareWidget?.title || 'Compare'} />}
            </BarChart>
          </ResponsiveContainer>
        );
      }
      case 'line_chart': {
        const rows = applyCrossFilter(SAMPLE_DATA.claims_trend, 'month');
        const withCompare = buildCompareData(rows);
        const data = withCompare || rows;
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} onClick={(state) => state?.activePayload?.[0] && handleChartClick(state.activePayload[0].payload)}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#a1a1aa' }} axisLine={false} />
              <YAxis tick={{ fontSize: 9, fill: '#a1a1aa' }} axisLine={false} />
              <Line type="monotone" dataKey="claims" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3, style: { cursor: (isSource || clickActionType) ? 'pointer' : 'default' } }} />
              {withCompare && <Line type="monotone" dataKey="compare_value" stroke="#a78bfa" strokeDasharray="4 3" strokeWidth={2} dot={false} name={compareWidget?.title || 'Compare'} />}
            </LineChart>
          </ResponsiveContainer>
        );
      }
      case 'area_chart':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={applyCrossFilter(SAMPLE_DATA.premium_trend, 'month')}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#a1a1aa' }} axisLine={false} />
              <YAxis tick={{ fontSize: 9, fill: '#a1a1aa' }} axisLine={false} />
              <Area type="monotone" dataKey="Auto" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} strokeWidth={2} />
              <Area type="monotone" dataKey="Homeowners" stroke="#6366f1" fill="#6366f1" fillOpacity={0.2} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        );
      case 'pie_chart': {
        const rows = applyCrossFilter(SAMPLE_DATA.loss_ratio, 'name');
        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={rows} cx="50%" cy="50%" innerRadius={25} outerRadius={50} dataKey="value" strokeWidth={0}
                onClick={(payload) => handleChartClick(payload)}
                style={{ cursor: (isSource || clickActionType) ? 'pointer' : 'default' }}>
                {rows.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        );
      }
      case 'small_multiples': {
        const trellisBy = widget.config?.trellis_by || 'name';
        const primaryRows = SAMPLE_DATA.premium_trend;
        const trellisColumns = Object.keys(primaryRows[0] || {}).filter(k => k !== 'month');
        let facets;
        let data;
        if (trellisColumns.includes(trellisBy)) {
          facets = [trellisBy];
          data = primaryRows;
        } else if (trellisBy === 'name' || trellisBy === 'region') {
          facets = SAMPLE_DATA.loss_ratio.map(r => r.name);
          data = null;
        } else {
          facets = trellisColumns;
          data = primaryRows;
        }
        facets = facets.slice(0, 6);
        return (
          <div className={`grid gap-1 h-full min-h-[100px] ${facets.length <= 2 ? 'grid-cols-2' : facets.length <= 4 ? 'grid-cols-2 grid-rows-2' : 'grid-cols-3 grid-rows-2'}`}>
            {facets.map((facet, idx) => {
              const facetData = data
                ? primaryRows.map(r => ({ month: r.month, v: r[facet] }))
                : [{ name: facet, v: SAMPLE_DATA.loss_ratio.find(r => r.name === facet)?.value || 0 }];
              return (
                <div key={facet} className="flex flex-col min-w-[60px]" data-testid={`small-multiples-facet-${facet}`}>
                  <div className="text-[8px] text-muted-foreground text-center mb-0.5 truncate">{facet}</div>
                  <div className="flex-1 min-h-[60px]">
                    <ResponsiveContainer width="99%" height="99%" minWidth={40} minHeight={40}>
                      {data ? (
                        <LineChart data={facetData}>
                          <XAxis dataKey="month" hide />
                          <YAxis hide />
                          <Line type="monotone" dataKey="v" stroke={COLORS[idx % COLORS.length]} strokeWidth={1.5} dot={false} />
                        </LineChart>
                      ) : (
                        <BarChart data={facetData}>
                          <XAxis dataKey="name" hide />
                          <YAxis hide />
                          <Bar dataKey="v" fill={COLORS[idx % COLORS.length]} radius={[3, 3, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }
      case 'image': {
        const src = widget.config?.imageUrl;
        return (
          <div className="flex items-center justify-center h-full w-full">
            {src
              ? <img src={src} alt={widget.title || 'Image'} className="max-w-full max-h-full object-contain" data-testid={`widget-image-${widget.widget_id}`} />
              : <div className="text-[10px] text-muted-foreground">Set an image URL in properties</div>}
          </div>
        );
      }
      default:
        return (
          <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
            <Table className="w-4 h-4 mr-1" /> Data Table
          </div>
        );
    }
  };

  const computeDeltaLabel = () => {
    if (!compareWidget) return null;
    try {
      if (widget.widget_type === 'kpi' && compareWidget.widget_type === 'kpi') {
        const a = parseFloat(String(widget.config?.value || '').replace(/[^0-9.-]/g, ''));
        const b = parseFloat(String(compareWidget.config?.value || '').replace(/[^0-9.-]/g, ''));
        if (isFinite(a) && isFinite(b) && b !== 0) {
          const pct = ((a - b) / b) * 100;
          return `Δ ${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% vs ${compareWidget.title}`;
        }
      }
      const primary = widget.widget_type === 'bar_chart' ? SAMPLE_DATA.loss_ratio.map(r => r.value)
                    : widget.widget_type === 'line_chart' ? SAMPLE_DATA.claims_trend.map(r => r.claims)
                    : null;
      const cmp = compareWidget.widget_type === 'bar_chart' ? SAMPLE_DATA.loss_ratio.map(r => r.value)
                : compareWidget.widget_type === 'line_chart' ? SAMPLE_DATA.claims_trend.map(r => r.claims)
                : null;
      if (primary && cmp && primary.length && cmp.length) {
        const avgA = primary.reduce((s, x) => s + x, 0) / primary.length;
        const avgB = cmp.reduce((s, x) => s + x, 0) / cmp.length;
        if (avgB !== 0) {
          const pct = ((avgA - avgB) / avgB) * 100;
          return `Δ ${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% vs ${compareWidget.title}`;
        }
      }
    } catch (e) { /* silent */ }
    return null;
  };
  const deltaLabel = computeDeltaLabel();

  return (
    <div
      className={`glass-card rounded-2xl p-4 cursor-pointer transition-all duration-200 group relative ${isSelected ? 'ring-2 ring-primary border-primary/30' : 'hover:border-white/20'}`}
      onClick={() => onSelect(widget.widget_id)}
      data-testid={`builder-widget-${widget.widget_id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Move className="w-3.5 h-3.5 text-muted-foreground/70 cursor-grab" />
          <p className="text-xs font-heading font-semibold text-foreground truncate">{widget.title}</p>
        </div>
        <div className="flex items-center gap-1">
          {isSource && <span className="text-[8px] px-1 py-0.5 rounded bg-primary/15 text-primary border border-primary/30" title="Clicks publish cross-filter">XF↑</span>}
          {isReceiver && crossFilters?.length > 0 && <span className="text-[8px] px-1 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 animate-pulse" title="Cross-filter active">XF↓•</span>}
          {isReceiver && (!crossFilters || crossFilters.length === 0) && <span className="text-[8px] px-1 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30" title="Applies cross-filter when active">XF↓</span>}
          {clickActionType && <span className="text-[8px] px-1 py-0.5 rounded bg-indigo-500/15 text-indigo-300 border border-indigo-500/30" title={`Click: ${clickActionType}`}>⟶</span>}
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(widget.widget_id); }}
            className="p-1 rounded-md hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
            data-testid={`remove-widget-${widget.widget_id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div className="h-32">
        {renderChart()}
      </div>
      <div className="flex items-center justify-between mt-2">
        <Badge variant="secondary" className="text-[9px] bg-muted/50">{widget.widget_type.replace('_', ' ')}</Badge>
        {deltaLabel && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 border border-indigo-500/30" data-testid={`delta-label-${widget.widget_id}`}>{deltaLabel}</span>
        )}
      </div>
    </div>
  );
}

export default WidgetPreview;
